// src/Dashboard/pages/user/Lexique/components/LexiqueToolbar.jsx

import React from "react";
import {
  FaCheck,
  FaDownload,
  FaFilter,
  FaHeart,
  FaSearch,
  FaShareAlt,
  FaTimes,
} from "react-icons/fa";

export default function LexiqueToolbar({
  ui,
  availableLetters,
  debouncedQ,
  setDebouncedQ,
  updateUi,
  onReset,
  onShare,
  shareCopied,
  onExport,
  favoritesCount,
}) {
  const fieldClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white";

  const actionClass =
    "inline-flex h-10 items-center justify-center gap-2 rounded-2xl px-3 text-xs font-bold ring-1 transition hover:-translate-y-0.5";

  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
            <FaFilter />
            Filtres du lexique
          </div>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Recherchez un terme, filtrez par lettre ou affichez uniquement vos
            favoris.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => updateUi({ favOnly: !ui.favOnly })}
            className={`${actionClass} ${
              ui.favOnly
                ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900"
            }`}
          >
            <FaHeart />
            Favoris
            {favoritesCount > 0 && (
              <span className="rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] dark:bg-white/10">
                {favoritesCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={onExport}
            className={`${actionClass} bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900`}
          >
            <FaDownload />
            Exporter
          </button>

          <button
            type="button"
            onClick={onShare}
            className={`${actionClass} ${
              shareCopied
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900"
            }`}
          >
            {shareCopied ? <FaCheck /> : <FaShareAlt />}
            {shareCopied ? "Copié" : "Partager"}
          </button>

          <button
            type="button"
            onClick={onReset}
            className={`${actionClass} bg-slate-900 text-white ring-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:ring-white dark:hover:bg-slate-100`}
          >
            <FaTimes />
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Recherche
          </label>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={debouncedQ}
              onChange={(event) => setDebouncedQ(event.target.value)}
              placeholder="Japonais, traduction..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Lettre
          </label>

          <select
            value={ui.letter}
            onChange={(event) => updateUi({ letter: event.target.value })}
            className={fieldClass}
          >
            {availableLetters.map((letter) => (
              <option key={letter} value={letter}>
                {letter === "Toutes" ? "Toutes les lettres" : letter}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Tri
          </label>

          <select
            value={ui.sort}
            onChange={(event) => updateUi({ sort: event.target.value })}
            className={fieldClass}
          >
            <option value="jp">Japonais A-Z</option>
            <option value="fr">Français A-Z</option>
          </select>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <div className="flex w-max items-center gap-2 pb-1">
          {availableLetters.map((letter) => {
            const active = ui.letter === letter;

            return (
              <button
                key={letter}
                type="button"
                onClick={() => updateUi({ letter })}
                className={`rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition ${
                  active
                    ? "bg-[#013E8E] text-white ring-[#013E8E]"
                    : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-900"
                }`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}